import {
  readWorkflowPublishedInspection,
  type WorkflowPublishedInspection,
  type WorkflowPublishedInspectionSource,
  type WorkflowSavedPresence,
} from './workflow-published-inspection';
import type {
  WorkflowPublishedWorkFact,
  WorkflowPublishedWorkDetailRow,
  WorkflowPublishedWorkDetailGroup,
  WorkflowPublishedWorkRow,
  WorkflowPublishedWorkSection,
} from './workflow-published-work-inspection';
export type WorkflowPublishedStatusTarget =
  | { kind: 'workflow' }
  | { kind: 'node'; nodeIndex: number; nodeId: string | null };
export type WorkflowPublishedStatusSectionId =
  | 'businessStatus'
  | 'legacyTransitions'
  | 'legacyDecisions';
export type WorkflowPublishedStatusFact = WorkflowPublishedWorkFact;
export type WorkflowPublishedStatusDetailRow = WorkflowPublishedWorkDetailRow;
export type WorkflowPublishedStatusDetailGroup =
  WorkflowPublishedWorkDetailGroup;
export type WorkflowPublishedStatusRow = WorkflowPublishedWorkRow;
export interface WorkflowPublishedStatusSection extends Omit<
  WorkflowPublishedWorkSection,
  'id'
> {
  id: WorkflowPublishedStatusSectionId;
}
export interface WorkflowPublishedStatusInspection {
  context: string;
  state: 'available' | 'unavailable';
  provenance: WorkflowPublishedInspection['provenance'];
  target: WorkflowPublishedStatusTarget;
  issues: readonly string[];
  owner: {
    kind: 'workflow' | 'node';
    index: number | null;
    id: string | null;
    label: string;
    issues: readonly string[];
    raw: unknown;
  } | null;
  sections: Readonly<
    Record<WorkflowPublishedStatusSectionId, WorkflowPublishedStatusSection>
  >;
}
type Saved = Record<string, unknown>;
type Field = readonly [string, string];
const record = (raw: unknown): raw is Saved =>
  !!raw && typeof raw === 'object' && !Array.isArray(raw);
const object = (raw: unknown): Saved => (record(raw) ? raw : {});
const presence = (owner: Saved, key: string): WorkflowSavedPresence =>
  !Object.hasOwn(owner, key)
    ? 'absent'
    : owner[key] === null
      ? 'null'
      : 'value';
const copy = (raw: unknown): unknown => structuredClone(raw);
const text = (raw: unknown): raw is string =>
  typeof raw === 'string' && !!raw.trim();
function display(raw: unknown): string {
  if (raw === undefined) return 'Not declared';
  if (raw === null) return 'Explicitly null';
  if (typeof raw === 'string') return raw === '' ? 'Empty saved text' : raw;
  try {
    return JSON.stringify(raw) ?? 'Unsupported saved value';
  } catch {
    return 'Unsupported saved value';
  }
}
function facts(
  raw: unknown,
  fields: readonly Field[],
  prefix: string,
): WorkflowPublishedStatusFact[] {
  const value = object(raw);
  return fields.map(([key, label]) => ({
    id: `${prefix}:${key}`,
    label,
    presence: presence(value, key),
    value: display(value[key]),
    issues: [],
    raw: copy(value[key]),
  }));
}
function extraIssues(raw: unknown, fields: readonly string[]): string[] {
  return Object.keys(object(raw)).some((key) => !fields.includes(key))
    ? [
        'Additional saved properties are retained without interpreting their meaning.',
      ]
    : [];
}
function duplicateIssues(
  raw: unknown,
  index: number,
  all: readonly unknown[],
  key: string,
  label: string,
): string[] {
  const value = object(raw)[key];
  if (!text(value))
    return [
      `Saved ${label} at index ${index} is missing or has an unsupported shape.`,
    ];
  return all.filter((item) => object(item)[key] === value).length === 1
    ? []
    : [
        `Duplicate saved ${label}: ${value}. Every indexed declaration is retained independently.`,
      ];
}
function detail(
  raw: unknown,
  index: number,
  prefix: string,
  label: string,
  fields: readonly Field[],
  issues: readonly string[] = [],
): WorkflowPublishedStatusDetailRow {
  return {
    id: `${prefix}-${index}`,
    index,
    label,
    fields: facts(raw, fields, `${prefix}-${index}`),
    issues: [
      ...(!record(raw)
        ? [
            'This saved entry has an unsupported shape. Its original value is retained.',
          ]
        : []),
      ...issues,
    ],
    raw: copy(raw),
  };
}
function objectGroup(
  owner: Saved,
  key: string,
  label: string,
  prefix: string,
  fields: readonly Field[],
  issues: readonly string[] = [],
): WorkflowPublishedStatusDetailGroup {
  const raw = owner[key],
    savedPresence = presence(owner, key);
  const rows =
    savedPresence === 'value'
      ? [
          detail(raw, 0, `${prefix}:${key}`, label, fields, [
            ...issues,
            ...extraIssues(
              raw,
              fields.map(([name]) => name),
            ),
          ]),
        ]
      : [];
  return {
    id: `${prefix}:${key}`,
    label,
    presence: savedPresence,
    state:
      savedPresence !== 'value'
        ? 'absent'
        : rows.some((row) => row.issues.length)
          ? 'retained-unknown'
          : 'available',
    rows,
    issues: [],
    raw: copy(raw),
  };
}
function collection(
  owner: Saved,
  key: string,
  label: string,
  prefix: string,
  project: (
    raw: unknown,
    index: number,
    all: readonly unknown[],
  ) => WorkflowPublishedStatusDetailRow,
): WorkflowPublishedStatusDetailGroup {
  const raw = owner[key],
    savedPresence = presence(owner, key),
    rows = Array.isArray(raw)
      ? raw.map((item, index) => project(item, index, raw))
      : [];
  const issues =
    savedPresence === 'value' && !Array.isArray(raw)
      ? [
          'This saved collection has an unsupported shape. Its original value is retained.',
        ]
      : [];
  return {
    id: `${prefix}:${key}`,
    label,
    presence: savedPresence,
    state:
      savedPresence !== 'value'
        ? 'absent'
        : issues.length || rows.some((row) => row.issues.length)
          ? 'retained-unknown'
          : rows.length
            ? 'available'
            : 'empty',
    rows,
    issues,
    raw: copy(raw),
  };
}
function strings(
  owner: Saved,
  key: string,
  label: string,
  prefix: string,
): WorkflowPublishedStatusDetailGroup {
  return collection(owner, key, label, prefix, (raw, index, all) => ({
    id: `${prefix}:${key}-${index}`,
    index,
    label: `Saved value ${index + 1}`,
    fields: [
      {
        id: `${prefix}:${key}-${index}:value`,
        label: 'Value',
        presence: 'value',
        value: display(raw),
        issues: [],
        raw: copy(raw),
      },
    ],
    issues: [
      ...(!text(raw) ? ['This saved value is not nonempty text.'] : []),
      ...(text(raw) && all.filter((item) => item === raw).length > 1
        ? [
            `Duplicate saved value: ${raw}. Its order and occurrences are retained.`,
          ]
        : []),
    ],
    raw: copy(raw),
  }));
}
const labels: Record<WorkflowPublishedStatusSectionId, string> = {
  businessStatus: 'Business labels',
  legacyTransitions: 'Legacy status changes',
  legacyDecisions: 'Legacy decisions',
};
function row(
  raw: unknown,
  index: number,
  prefix: string,
  label: string,
  fields: readonly Field[],
  groups: readonly WorkflowPublishedStatusDetailGroup[] = [],
  issues: readonly string[] = [],
): WorkflowPublishedStatusRow {
  const base = detail(raw, index, prefix, label, fields, issues);
  return {
    ...base,
    description:
      'Literal saved declarations only. No current status, engine transition, default decision, recipient or notification is evaluated.',
    groups,
    state:
      base.issues.length ||
      groups.some((group) => group.state === 'retained-unknown')
        ? 'retained-unknown'
        : 'available',
  };
}
function section(
  owner: Saved,
  key: string,
  id: WorkflowPublishedStatusSectionId,
  rows: WorkflowPublishedStatusRow[],
  issues: readonly string[] = [],
): WorkflowPublishedStatusSection {
  const raw = owner[key],
    savedPresence = presence(owner, key);
  return {
    id,
    label: labels[id],
    presence: savedPresence,
    rows,
    issues,
    raw: copy(raw),
    state:
      savedPresence !== 'value'
        ? 'absent'
        : issues.length || rows.some((row) => row.state === 'retained-unknown')
          ? 'retained-unknown'
          : Array.isArray(raw) && !raw.length
            ? 'empty'
            : 'available',
  };
}
function unique(
  collection: unknown,
  key: string,
  id: unknown,
): { raw: Saved; index: number } | null {
  if (!Array.isArray(collection) || !text(id)) return null;
  const matches = collection
    .map((raw, index) => ({ raw, index }))
    .filter((item) => object(item.raw)[key] === id);
  return matches.length === 1 && record(matches[0].raw)
    ? { raw: matches[0].raw, index: matches[0].index }
    : null;
}
function targetFacts(
  prefix: string,
  label: string,
  target: ReturnType<typeof unique>,
  caption: unknown,
): WorkflowPublishedStatusFact[] {
  return target
    ? [
        {
          id: `${prefix}:index`,
          label: `Saved ${label} index`,
          presence: 'value',
          value: String(target.index),
          issues: [],
          raw: target.index,
        },
        {
          id: `${prefix}:target`,
          label: `Unique saved ${label}`,
          presence: 'value',
          value: display(caption),
          issues: [],
          raw: copy(target.raw),
        },
      ]
    : [];
}
const messageFields: readonly Field[] = [
  ['version', 'Message version'],
  ['contextProfileCode', 'Context profile code'],
  ['contextProfileVersion', 'Context profile version'],
  ['titleTemplate', 'Saved title template'],
  ['bodyTemplate', 'Saved body template'],
];
const decisionFields: readonly Field[] = [
  ['decisionKind', 'Saved decision kind'],
  ['requiredOutcomeCode', 'Saved required outcome'],
  ['ownerQueueCode', 'Saved owner queue'],
];
const decisionKeys: readonly Field[] = [
  ...decisionFields,
  ['reaction', 'Reaction'],
  ['eligibleRoleCodes', 'Eligible roles'],
  ['notifications', 'Notifications'],
  ['notificationMessage', 'Notification message'],
];
function notificationGroups(
  owner: Saved,
  prefix: string,
  parent: string,
): WorkflowPublishedStatusDetailGroup[] {
  const recipients = collection(
    owner,
    'notifications',
    `${parent}: Saved notification recipients`,
    prefix,
    (raw, index, all) =>
      detail(
        raw,
        index,
        `${prefix}:notification`,
        `${parent}: Recipient ${index + 1}`,
        [
          ['id', 'Recipient rule ID'],
          ['recipientTypeCode', 'Recipient type'],
          ['recipientTargetKey', 'Recipient target key'],
          ['severityCode', 'Severity code'],
        ],
        duplicateIssues(raw, index, all, 'id', 'notification ID'),
      ),
  );
  const raw = owner['notificationMessage'],
    value = object(raw);
  const message = objectGroup(
    owner,
    'notificationMessage',
    `${parent}: Saved notification message`,
    prefix,
    messageFields,
    raw != null &&
      (value['version'] !== 1 ||
        !text(value['contextProfileCode']) ||
        !Number.isSafeInteger(value['contextProfileVersion']) ||
        Number(value['contextProfileVersion']) < 1 ||
        typeof value['titleTemplate'] !== 'string' ||
        typeof value['bodyTemplate'] !== 'string')
      ? [
          'The saved message contract is unsupported. Templates are retained as text without rendering or profile lookup.',
        ]
      : [],
  );
  return [recipients, message];
}
function decisionGroups(
  raw: unknown,
  prefix: string,
  parent: string,
): WorkflowPublishedStatusDetailGroup[] {
  const value = object(raw);
  const reaction = objectGroup(
    value,
    'reaction',
    `${parent}: Saved reaction`,
    prefix,
    [
      ['reactionCode', 'Saved reaction code'],
      ['targetStateCode', 'Saved target state'],
    ],
  );
  const roles = strings(
    value,
    'eligibleRoleCodes',
    `${parent}: Saved eligible role codes`,
    prefix,
  );
  return [reaction, roles, ...notificationGroups(value, prefix, parent)];
}
function decisions(
  owner: Saved,
  prefix: string,
  parent: string,
): WorkflowPublishedStatusDetailGroup[] {
  const raw = owner['requiredDecisions'];
  const metadata = collection(
    owner,
    'requiredDecisions',
    `${parent}: Saved structured decisions`,
    prefix,
    (item, index) => {
      const value = object(item),
        current = detail(
          item,
          index,
          `${prefix}:decision`,
          `${parent} · Decision ${index + 1} · Kind ${display(value['decisionKind'])}`,
          decisionKeys,
        );
      current.fields = facts(
        item,
        decisionFields,
        `${prefix}:decision-${index}`,
      );
      return current;
    },
  );
  const groups: WorkflowPublishedStatusDetailGroup[] = [metadata];
  if (Array.isArray(raw))
    raw.forEach((item, index) =>
      groups.push(
        ...decisionGroups(
          item,
          `${prefix}:decision-${index}`,
          `${parent} · Decision ${index + 1} · Kind ${display(object(item)['decisionKind'])}`,
        ),
      ),
    );
  return groups;
}
function legacyTransitions(
  owner: Saved,
  scope: string,
  label: string,
  target: WorkflowPublishedStatusTarget,
): WorkflowPublishedStatusSection {
  const key =
      target.kind === 'workflow' ? 'workflowTransitions' : 'allowedTransitions',
    raw = owner[key];
  const fields: readonly Field[] = [
    ['fromStateCode', 'Saved from state'],
    ['toStateCode', 'Saved to state'],
  ];
  const rows = Array.isArray(raw)
    ? raw.map((item, index) => {
        const value = object(item),
          prefix = `${scope}:transition-${index}`,
          parent = `${label} · Transition ${index + 1} · ${display(value['fromStateCode'])} → ${display(value['toStateCode'])}`;
        const groups = [
          strings(
            value,
            'requiredDecisionKinds',
            `${parent}: Legacy decision kind strings`,
            prefix,
          ),
          ...decisions(value, prefix, parent),
          ...notificationGroups(value, prefix, parent),
        ];
        const current = row(
          item,
          index,
          `${scope}:transition`,
          parent,
          [
            ...fields,
            ['requiredDecisionKinds', 'Legacy decision kinds'],
            ['requiredDecisions', 'Structured decisions'],
            ['notifications', 'Notifications'],
            ['notificationMessage', 'Notification message'],
          ],
          groups,
        );
        current.fields = facts(item, fields, prefix);
        return current;
      })
    : [];
  return section(
    owner,
    key,
    'legacyTransitions',
    rows,
    presence(owner, key) === 'value' && !Array.isArray(raw)
      ? [
          'The saved transition collection is opaque. No empty collection or engine changes are inferred.',
        ]
      : [],
  );
}
function legacyDecisions(
  owner: Saved,
  scope: string,
  label: string,
): WorkflowPublishedStatusSection {
  const raw = owner['requiredDecisions'];
  const rows = Array.isArray(raw)
    ? raw.map((item, index) => {
        const prefix = `${scope}:direct-decision-${index}`,
          parent = `${label} · Direct decision ${index + 1} · Kind ${display(object(item)['decisionKind'])}`;
        const current = row(
          item,
          index,
          `${scope}:direct-decision`,
          parent,
          decisionKeys,
          decisionGroups(item, prefix, parent),
        );
        current.fields = facts(item, decisionFields, prefix);
        return current;
      })
    : [];
  return section(
    owner,
    'requiredDecisions',
    'legacyDecisions',
    rows,
    presence(owner, 'requiredDecisions') === 'value' && !Array.isArray(raw)
      ? [
          'The saved direct decision collection is opaque. No default decision or approval is inferred.',
        ]
      : [],
  );
}
function businessStatus(
  owner: Saved,
  document: Saved,
  scope: string,
  workflow: boolean,
  ownerUnique: boolean,
): WorkflowPublishedStatusSection {
  const raw = owner['statusConfiguration'],
    configuration = object(raw),
    prefix = `${scope}:business-status`,
    fields: readonly Field[] = [
      ['initialStatusCode', 'Saved initial status code'],
      ['allowManualChanges', 'Saved manual changes setting'],
    ];
  const statuses = collection(
    configuration,
    'statuses',
    'Saved business status labels',
    prefix,
    (item, index, all) =>
      detail(
        item,
        index,
        `${prefix}:status`,
        text(object(item)['label'])
          ? display(object(item)['label'])
          : `Saved status ${index + 1}`,
        [
          ['code', 'Saved status code'],
          ['label', 'Saved status label'],
        ],
        duplicateIssues(item, index, all, 'code', 'status code'),
      ),
  );
  const ruleFields: readonly Field[] = [
    ['id', 'Rule ID'],
    ['eventCode', 'Saved event code'],
    ['statusCode', 'Saved target status code'],
    ['requirementId', 'Saved requirement ID'],
    ['sourceNodeId', 'Saved source node ID'],
    ['outcomeCode', 'Saved outcome code'],
  ];
  const rules = collection(
    configuration,
    'rules',
    'Saved business status rules',
    prefix,
    (item, index, all) => {
      const rule = object(item),
        rulePrefix = `${prefix}:rule-${index}`,
        status = unique(configuration['statuses'], 'code', rule['statusCode']);
      const source = workflow
        ? unique(document['nodes'], 'id', rule['sourceNodeId'])
        : ownerUnique
          ? { raw: owner, index: 0 }
          : null;
      const requirements = source
        ? object(object(source.raw['declaration'])['behavior'])[
            'completionRequirements'
          ]
        : null;
      const requirement = unique(requirements, 'id', rule['requirementId']);
      const current = detail(
        item,
        index,
        `${prefix}:rule`,
        text(rule['id']) ? display(rule['id']) : `Saved rule ${index + 1}`,
        ruleFields,
        [
          ...duplicateIssues(item, index, all, 'id', 'status rule ID'),
          ...(!status
            ? [
                'The saved status code is missing or ambiguous in this exact owner. No label is substituted.',
              ]
            : []),
          ...(workflow && rule['sourceNodeId'] != null && !source
            ? [
                'The saved source node is missing or ambiguous. No step is substituted.',
              ]
            : []),
          ...(!workflow && rule['sourceNodeId'] != null
            ? [
                'A saved source node is retained literally; this rule belongs to the exact selected node.',
              ]
            : []),
          ...(rule['requirementId'] != null && !requirement
            ? [
                'The exact saved source does not identify one requirement. No provider or requirement is inferred.',
              ]
            : []),
        ],
      );
      current.fields = [
        ...current.fields,
        ...targetFacts(
          `${rulePrefix}:status`,
          'status label',
          status,
          status?.raw['label'],
        ),
        ...(workflow
          ? targetFacts(
              `${rulePrefix}:source`,
              'source node',
              source,
              source?.raw['displayName'],
            )
          : []),
        ...targetFacts(
          `${rulePrefix}:requirement`,
          'requirement',
          requirement,
          requirement?.raw['id'],
        ),
      ];
      return current;
    },
  );
  const initial = unique(
    configuration['statuses'],
    'code',
    configuration['initialStatusCode'],
  );
  const rows =
    presence(owner, 'statusConfiguration') === 'value'
      ? [
          row(
            raw,
            0,
            prefix,
            'Saved business status configuration',
            [...fields, ['statuses', 'Statuses'], ['rules', 'Rules']],
            [statuses, rules],
            [
              ...(!workflow && owner['typeCode'] !== 'human_step'
                ? [
                    'Business statuses are saved on a different node kind. They are retained without relocation.',
                  ]
                : []),
              ...(!initial
                ? [
                    'The saved initial status code is missing or ambiguous. No starting status is generated.',
                  ]
                : []),
              ...(typeof configuration['allowManualChanges'] !== 'boolean'
                ? [
                    'The saved manual changes setting is unsupported. No default is supplied.',
                  ]
                : []),
            ],
          ),
        ]
      : [];
  if (rows.length)
    rows[0].fields = [
      ...facts(raw, fields, `${prefix}-0`),
      ...targetFacts(
        `${prefix}:initial`,
        'initial status label',
        initial,
        initial?.raw['label'],
      ),
    ];
  return section(owner, 'statusConfiguration', 'businessStatus', rows);
}
/** Literal saved business labels and legacy checks; canonical status is reported, never recomputed or evaluated. */
export function readWorkflowPublishedStatusInspection(
  source: WorkflowPublishedInspectionSource,
  target: WorkflowPublishedStatusTarget,
): WorkflowPublishedStatusInspection {
  const admitted = readWorkflowPublishedInspection(source);
  const unavailable = (
    id: WorkflowPublishedStatusSectionId,
  ): WorkflowPublishedStatusSection => ({
    id,
    label: labels[id],
    state: 'unavailable',
    presence: 'absent',
    rows: [],
    issues: [],
    raw: null,
  });
  const sections: Record<
    WorkflowPublishedStatusSectionId,
    WorkflowPublishedStatusSection
  > = {
    businessStatus: unavailable('businessStatus'),
    legacyTransitions: unavailable('legacyTransitions'),
    legacyDecisions: unavailable('legacyDecisions'),
  };
  const result: WorkflowPublishedStatusInspection = {
    context: JSON.stringify([
      admitted.context,
      target,
      source.definitionDocument,
    ]),
    state: 'unavailable',
    provenance: structuredClone(admitted.provenance),
    target: { ...target },
    owner: null,
    issues: [...admitted.issues],
    sections,
  };
  if (admitted.state !== 'available') {
    result.issues = [
      ...admitted.issues,
      'Saved business labels and legacy checks are withheld because the canonical document was not admitted.',
    ];
    return result;
  }
  const document = object(source.definitionDocument);
  let owner: Saved,
    scope: string,
    label: string,
    ownerRaw: unknown,
    index: number | null = null,
    id: string | null = null,
    ownerUnique = true;
  if (target.kind === 'workflow') {
    owner = document;
    scope = 'workflow';
    id =
      typeof document['workflowCode'] === 'string'
        ? document['workflowCode']
        : null;
    label = 'Workflow';
    const raw: Saved = {};
    for (const key of [
      'statusConfiguration',
      'workflowTransitions',
      'requiredDecisions',
    ])
      if (Object.hasOwn(document, key)) raw[key] = copy(document[key]);
    ownerRaw = raw;
  } else {
    const nodes = document['nodes'];
    index = target.nodeIndex;
    if (
      !Number.isSafeInteger(index) ||
      index < 0 ||
      !Array.isArray(nodes) ||
      index >= nodes.length
    ) {
      result.issues = [
        'The exact saved node index is unavailable. No other owner is substituted.',
      ];
      return result;
    }
    const raw = nodes[index];
    owner = object(raw);
    id = typeof owner['id'] === 'string' ? owner['id'] : null;
    if (id !== target.nodeId) {
      result.issues = [
        'The saved node index and identity do not match. Labels and checks are withheld.',
      ];
      return result;
    }
    scope = `node-${index}`;
    label = `Node ${index + 1} · ID ${display(owner['id'])}`;
    ownerRaw = raw;
    ownerUnique = unique(nodes, 'id', id) !== null;
    result.issues = [
      ...(!ownerUnique
        ? [
            'The saved node ID is missing or duplicated. Indexed declarations remain; cross-reference ownership is not guessed.',
          ]
        : []),
      ...(!record(raw)
        ? [
            'The exact saved node is opaque. No absent policy declarations are inferred.',
          ]
        : []),
    ];
  }
  result.state = 'available';
  result.owner = {
    kind: target.kind,
    index,
    id,
    label,
    issues: [...result.issues],
    raw: copy(ownerRaw),
  };
  if (target.kind === 'node' && !record(ownerRaw)) {
    for (const key of [
      'businessStatus',
      'legacyTransitions',
      'legacyDecisions',
    ] as const)
      sections[key] = {
        ...sections[key],
        state: 'retained-unknown',
        presence: 'value',
        issues: [
          'The saved node is opaque. Its original value is retained without substituting empty settings.',
        ],
        raw: copy(ownerRaw),
      };
    return result;
  }
  sections.businessStatus = businessStatus(
    owner,
    document,
    scope,
    target.kind === 'workflow',
    ownerUnique,
  );
  sections.legacyTransitions = legacyTransitions(owner, scope, label, target);
  sections.legacyDecisions = legacyDecisions(owner, scope, label);
  return result;
}
